import * as React from 'react';
import { useAuth } from '@components/amplifyProvider';
import AvCopilot from '@components/Copilot/AvCopilot';
import { datadogRum } from '@datadog/browser-rum';
import { loadingPage } from '@fabric-ui/components';
import { envVariables } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { getAccountTokenFromLS } from '@utils/auth.utils';
import { TanStackRouterDevtools } from '@utils/AvRouter';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import AvAppBar from '../components/newNavigation/AvAppBar';
import AvSidebar from '../components/newNavigation/AvSideBar';
import { useAvContext } from '../context/AvContextProvider';
import { useAvCopilotContext } from '../context/AvCopilotProvider';
import { useAvDebugContext } from '../context/AvDebugProvider';
import RootContext from '../context/RootContext';
import RouteLoadingProvider from '../context/RouteLoadingProvider';
import { useAppVersion } from '../hooks/useAppVersion';
import { RouterContextType } from '../RouterContext';

export const Route = createRootRouteWithContext<RouterContextType>()({
  component: Rootroot,
  beforeLoad: () => ({
    hideBreadcrumbs: false,
    getTitle: () => '',
  }),
});
function Rootroot() {
  return (
    <RootContext>
      <RootComponent />
    </RootContext>
  );
}

export const Main = ({ children }: React.PropsWithChildren) => (
  <Box component="main" aria-label="workspace" sx={{ gridArea: 'main' }}>
    {children}
  </Box>
);

function RootComponent() {
  const {
    isLoading,
    isAuthenticated,
    accountId,
    accountData: { aiFeatures },
  } = useAvContext();
  const { isOpen } = useAvCopilotContext();
  const { showDevTools } = useAvDebugContext();
  const isLoggedIn = localStorage.getItem(AuthStorageKeys.authConfig);
  const { logout } = useAuth();
  useAppVersion();
  if (!isAuthenticated) {
    if (isLoading) {
      if (!getAccountTokenFromLS(accountId)) {
        return loadingPage(!!isLoggedIn);
      }
    } else if (envVariables.MODE !== 'development') {
      const accessToken = getAccountTokenFromLS(accountId);
      if (!accessToken) {
        datadogRum.addAction(`logout was triggered from _root, no access token after retry`, { accountId });
      }
      logout(!!accessToken);
      return loadingPage(!!isLoggedIn);
    }
  }
  return (
    <>
      <AvAppBar />
      <AvSidebar />
      <Main>
        <RouteLoadingProvider>
          <Outlet />
        </RouteLoadingProvider>
      </Main>
      {aiFeatures?.enabled && isOpen && <AvCopilot />}
      {showDevTools && <TanStackRouterDevtools />}
    </>
  );
}
